import { uniqBy } from "lodash-es";
import { type VFC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { CardGridArea } from "widgets";

import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { type User } from "../../graphql.js";
import { type IContigState, shouldLoadContig } from "../../redux/modules/contig.js";
import { type IAppState } from "../../redux/reducer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { type IPageHeaderProps } from "../core/navigation-panel/PageHeader.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";
import { ContigAnnotationCard } from "./cards/ContigAnnotationCard.js";
import { ContigCard } from "./cards/ContigCard.js";
import { DatabaseCard } from "./cards/DatabaseCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import { IntReferencesCard } from "./cards/IntReferencesCard.js";
import { RelatedProjectsCard } from "./cards/RelatedProjectsCard.js";
import { renderContigAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { EventStatusCard } from "@/ui-modules/playbooks/events/EventStatusCard.js";

interface IContigSummaryPageOwnProps {
  contigId: number; // In case the path is not "/contig/:contigId" @see BlastResultHitSummaryView
  title?: string;
  pageHeaderProps?: IPageHeaderProps;
  blastContext?: boolean;
}

const ContigSummaryView: VFC<IContigSummaryPageOwnProps> = (props) => {
  const { contigId } = useParams<"contigId">();
  const { contig, isLoading } = useSelector<IAppState, IContigState>((state) => state.contig);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadContig, props.contigId ?? contigId);

  useEffect(() => {
    void load();
  }, [load]);

  const showSpinner = isLoading || !contig;

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (contig ? contig.sequenceId : "")}
      subtitle={contig?.description ?? "Contig Summary"}
      entity={isLoading ? null : contig}
      headerTags={props.pageHeaderProps?.tags}
      project={props.pageHeaderProps?.project}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderContigAttributesWidgets(contig)}
          key={"contig-summary-view-widget-1"}
        />,
      ]}
      isLoading={showSpinner}
      getEntityName={() => contig?.sequenceId}
    >
      {contig && (
        <>
          <ContigCard contig={contig} area={CardGridArea.Left} blastContext={props.blastContext} />
          <GenomeCard genome={contig.organism} area={CardGridArea.Left} />
          <DatabaseCard genome={contig.organism} database={contig.database} area={CardGridArea.Left} />
          <ContigAnnotationCard contig={contig} area={CardGridArea.Right} />
          {/*<GeneCard gene={contig.gene} position={{ x: 1, y: 0}}  title={'Parent Gene'}/>*/}
          {/*<ProteinCard protein={contig.protein} position={{ x: 1, y: 1}} title={"Related Protein"}/>*/}
          {/*<LocationCard contigLocations={contig.contigLocations} position={{ x: 1, y: 2}}/>*/}
          <IntReferencesCard references={contig.internalReferences} area={CardGridArea.Right} />
          <EventStatusCard organismId={contig.organism.id} area={CardGridArea.Panel} />
          <RelatedProjectsCard
            relatedProjects={contig.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default ContigSummaryView;
